<template>
  <metainfo>
    <template v-slot:title="{ content }">{{
      content ? `Forsis | ${content}` : `Forsis`
    }}</template>
  </metainfo>
  <LoadingOverlay v-if="isLoading" />

  <Dialog :visible="generalDialogVisible" :modal="generalDialogBackdrop">
    <template #container>
      <div
        class="general-modal-container flex flex-column justify-content-center align-items-center"
      >
        <p class="mb-3">
          {{ generalDialogMsg }}
        </p>
        <div class="text-center">
          <Button
            class="mx-auto"
            label="Ok"
            icon="pi pi-check"
            @click="closeDialog()"
            autofocus
            size="small"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <ConfirmDialog>
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div
        class="flex flex-column align-items-center p-5 surface-overlay border-round"
      >
        <div
          class="border-circle bg-primary inline-flex justify-content-center align-items-center h-6rem w-6rem -mt-8"
        >
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-4">{{
          message.header
        }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex align-items-center gap-2 mt-4">
          <Button label="Save" @click="acceptCallback" class="w-8rem"></Button>
          <Button
            label="Cancel"
            outlined
            @click="rejectCallback"
            class="w-8rem"
          ></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <router-view />
</template>

<script>
import LoadingOverlay from "@/views/components/LoadingOverlay.vue";
import { useMeta } from "vue-meta";
import { mapWritableState, mapActions } from "pinia";
import { useGeneralStore } from "@/stores/GeneralStore";
import { useUserStore } from "@/stores/UserStore";

import Dialog from "primevue/dialog";
import ConfirmDialog from "primevue/dialog";
import Button from "primevue/button";

import html2pdf from "html2pdf.js";
import config from "./configs/config";

export default {
  components: {
    LoadingOverlay,
    Dialog,
    Button,
    ConfirmDialog,
  },
  computed: {
    ...mapWritableState(useGeneralStore, [
      "idleTime",
      "lastOpened",
      "isLoading",
      "generalDialogVisible",
      "generalDialogMsg",
      "generalDialogBackdrop",
      "choosedLanguage",
      "activeOwner",
      "isLogout"
    ]),
    ...mapWritableState(useUserStore, ["user_id"]),
  },
  watch: {},
  methods: {
    ...mapActions(useGeneralStore, [
      "setActiveOwner",
      "addIdleTime",
      "resetIdleTime",
      "showGeneralDialog",
      "getIsAi"
    ]),
    ...mapActions(useUserStore, ["userLogout"]),

    closeDialog() {
      this.generalDialogVisible = false;
    },

    lang(key) {
      return this.languages[this.choosedLanguage][key];
    },

    langIsIndo() {
      return this.choosedLanguage == "indonesia" ? true : false;
    },

    formValidation(forms) {
      let isValid = true;
      for (const key in forms) {
        if (forms[key].validation) {
          let formValue = forms[key].value;

          forms[key].warning = null;
          for (let i = 0; i < forms[key].validation.length; i++) {
            let v = forms[key].validation[i];

            let validationRes = v(formValue);
            if (validationRes != null) {
              forms[key].warning = validationRes;
              isValid = false;
            }
          }
        }
      }
      return isValid;
    },

    preventArrowKeyIncrement(event) {
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
      }
    },

    confirmDeleteDialog() {
      return new Promise((resolve) => {
        this.$confirm.require({
          message: this.$root.lang("confirm_back"),
          header: "Confirmation",
          accept: () => {
            this.lastPath = "";
            resolve(true);
          },
          reject: () => {
            resolve(false);
          },
          acceptLabel: "Yes",
          rejectLabel: "No",
          rejectIcon: "pi pi-times",
          acceptIcon: "pi pi-check",
          icon: "pi pi-exclamation-triangle",
        });
      });
    },

    printPreviewComponent(idComp, filename, orientation) {
      const vm = this;
      const elementToConvert = document.getElementById(idComp);
      console.log(elementToConvert)
      const options = {
        margin: [8, 8, 8, 8],
        filename: `${filename}.pdf`,
        pagebreak: {
          mode: ["avoid-all", "css", "legacy"],
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: orientation ? orientation : "portrait",
        },
        html2canvas: { 
          dpi: 192,
          scale: 3,
          letterRendering: true,
          useCORS: true
        }
      };

      vm.isLoading = true;

      html2pdf()
        .set(options)
        .from(elementToConvert)
        .toPdf()
        .get("pdf")
        .then((pdf) => {
          var totalPages = pdf.internal.getNumberOfPages();

          for (var i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(7);
            pdf.setFont("", "bold");
            pdf.setTextColor("black");
            pdf.text(
              `Printed on: ${this.formatDate(
                new Date(),
                "datetime"
              )}. Page ${i} of ${totalPages}`,
              pdf.internal.pageSize.getWidth() - 65,
              pdf.internal.pageSize.getHeight() - 10
            );
          }

          vm.showPrintDialog = false;
          vm.isLoading = false;
        })
        .catch((error) => {
          console.error("Error generating PDF:", error);

          vm.showPrintDialog = false;
          vm.isLoading = false;
        })
        .save();
    },

    setTheme() {
      const faviconLink = document.createElement("link");
      faviconLink.rel = "icon";
      faviconLink.href = require(`./assets/images/icons/${this.activeOwner.iconFile}`);
      document.head.appendChild(faviconLink);

      this.activeOwner.cssFile.forEach((file) => {
        import(`./assets/css/${file}`);
      });
    },

    setIdleMechanism() {
      const vm = this;

      setInterval(() => {
        vm.checkIdleTime();
      }, 1000);

      document.onmousemove = () => {
        vm.resetIdleTime();
      };
      document.onkeydown = () => {
        vm.resetIdleTime();
      };
    },

    checkIdleTime() {
      const vm = this;
      vm.addIdleTime();
      if (vm.idleTime > 3600) {
        vm.logout();
      }
    },

    checkLastOpened() {
      let lastOpened = new Date(this.lastOpened);
      let curDate = new Date();

      let interval = curDate.getTime() - lastOpened.getTime();
      let intervalSecond = interval / 1000;

      if (intervalSecond > 3600) {
        this.logout();
      }
    },

    async logout() {
      const vm = this;
      this.lastOpened = null;
      this.idleTime = 0;

      if (vm.user_id) {
        this.showGeneralDialog(`${this.$root.lang("session_expired")}`, true);
        await vm.userLogout();

        if (vm.$router.path != "/") {
          vm.$router.push({ path: "/" });
        }
      }
    },

    formatDate(date) {
      const vm = this;
      return vm.myPlugin.formatDate(date, "datetime");
    },
  },
  mounted() {
    this.setActiveOwner();
    this.setTheme();
    this.getIsAi();

    // IDLE MECHANISM IS CLOSED FOR A WHILE
    // this.setIdleMechanism();

    // window.onbeforeunload = () => {
    //   this.lastOpened = new Date().getTime();
    // };

    // this.checkLastOpened();
  },
  setup() {
    const owner = config.owner.find((own) => own.id == config.activeOwner);
    useMeta({
      title: "",
      htmlAttrs: { lang: "en", amp: true },
      meta: [
        {
          name: "title",
          content: owner.meta.title,
        },
        {
          name: "description",
          content: owner.meta.description,
        },
        {
          name: "image",
          content: owner.meta.image,
        },
      ],
      og: {
        title: owner.meta.title,
        description: owner.meta.description,
        image: owner.meta.image,
      },
    });
  },
};
</script>

<style>
::placeholder,
.p-placeholder {
  /* Most modern browsers support this now. */
  color: rgba(80, 80, 80, 0.4);
}

html,
body {
  height: 100%;
  width: 100%;
  margin: 0 !important;
  overflow: hidden;
}
#app {
  height: 100%;
  width: 100%;
}

.flex-auto {
  flex: auto;
}

.flex-initial {
  flex: initial;
}

.general-modal-container {
  border-radius: 5px;
  background-color: white;
  padding: 20px;
  min-width: 350px;
  min-height: 200px;
}

.p-autocomplete-input {
  max-width: 100%;
}

.p-inputtext {
  max-width: 100%;
}

span .p-inputtext {
  padding: 0.75rem;
}

.p-paginator .p-inputtext {
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
}
</style>
